import React from "react";
import { 
	Email, 
	Phone, 
	Business, 
	Badge, 
	LocationOn, 
	Language, 
	Work, 
	Tag,
	Person,
	PhoneAndroid,
	HomeWork,
	Public,
	Category,
	Description
} from "@mui/icons-material";
import type { ProfileDisplayProps } from "../types";
import { Typography, Divider, Box } from "@mui/material";

const DisplayField: React.FC<{ icon: React.ReactNode; label: string; value?: string | null }> = ({ icon, label, value }) => {
	const displayValue = value || "N/A";
	const [isExpanded, setIsExpanded] = React.useState(false);
	const shouldShowToggle = displayValue.length > 90;
	return (
		<div className="flex items-start bg-[#F7F9FB] rounded-xl px-4 py-3 overflow-hidden h-full">
			<div className="mr-3 text-gray-600 mt-0.5 shrink-0">{icon}</div>
			<div className="flex flex-col min-w-0 flex-1">
				<span className="text-xs text-gray-500 font-medium">{label}</span>
				<span
					className="text-base text-gray-900 block"
					title={displayValue}
					style={{
						overflowWrap: "anywhere",
						wordBreak: "break-word",
						whiteSpace: isExpanded ? "normal" : "initial",
						overflow: "hidden",
						textOverflow: "ellipsis",
						display: isExpanded ? "block" : "-webkit-box",
						WebkitLineClamp: isExpanded ? "unset" : 2,
						WebkitBoxOrient: "vertical",
						minHeight: isExpanded ? "auto" : "3rem",
					}}>
					{displayValue}
				</span>
				{shouldShowToggle && (
					<button
						type="button"
						onClick={() => setIsExpanded(prev => !prev)}
						className="mt-1 text-xs font-medium text-primary-50 self-start hover:underline">
						{isExpanded ? "View Less" : "View More"}
					</button>
				)}
			</div>
		</div>
	);
};

const ProfileDisplay: React.FC<ProfileDisplayProps> = ({ businessDetails }) => {
	// Get sector name directly from API data
	const getSectorName = () => {
		return businessDetails?.sector?.name || null;
	};

	return (
		<Box sx={{ overflowX: "hidden", width: "100%" }}>
			{/* Business Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				Business Details
			</Typography>
			
			<div className="grid grid-cols-2 gap-3 mb-3 items-stretch">
				<DisplayField 
					icon={<Business className="w-5 h-5" />} 
					label="Name of Company / Firm" 
					value={businessDetails?.businessName} 
				/>
				<DisplayField 
					icon={<Phone className="w-5 h-5" />} 
					label="Office Landline Number" 
					value={businessDetails?.phoneNumber} 
				/>
			</div>

			<div className="grid grid-cols-2 gap-3 mb-3 items-stretch">
				<DisplayField 
					icon={<Language className="w-5 h-5" />} 
					label="Website/URL" 
					value={businessDetails?.businessWebsiteURL} 
				/>
				<DisplayField 
					icon={<Work className="w-5 h-5" />} 
					label="Principal Line of Business" 
					value={businessDetails?.businessPrincipalLine} 
				/>
			</div>

			<div className="mb-3">
				<DisplayField 
					icon={<LocationOn className="w-5 h-5" />} 
					label="Registered Office Address" 
					value={businessDetails?.address} 
				/>
			</div>

			<div className="mb-3">
				<DisplayField 
					icon={<Category className="w-5 h-5" />} 
					label="Sector" 
					value={getSectorName()} 
				/>
			</div>

			<Divider sx={{ my: 3 }} />

			{/* Contact Person Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				Contact Person Details
			</Typography>

			<div className="grid grid-cols-2 gap-3 mb-3 items-stretch">
				<DisplayField 
					icon={<Person className="w-5 h-5" />} 
					label="Full Name" 
					value={businessDetails?.person?.fullName} 
				/>
				<DisplayField 
					icon={<Badge className="w-5 h-5" />} 
					label="CNIC" 
					value={businessDetails?.person?.cnic} 
				/>
			</div>

			<div className="grid grid-cols-2 gap-3 mb-3 items-stretch">
				<DisplayField 
					icon={<Phone className="w-5 h-5" />} 
					label="Landline Number" 
					value={businessDetails?.person?.landline} 
				/>
				<DisplayField 
					icon={<PhoneAndroid className="w-5 h-5" />} 
					label="Mobile Number" 
					value={businessDetails?.person?.mobile} 
				/>
			</div>

			<div className="grid grid-cols-2 gap-3 mb-3">
				<DisplayField 
					icon={<Email className="w-5 h-5" />} 
					label="Personal Email" 
					value={businessDetails?.person?.email} 
				/>
				<DisplayField 
					icon={<Email className="w-5 h-5" />} 
					label="Business Email" 
					value={businessDetails?.businessEmailAddress} 
				/>
			</div>

			<div className="mb-3">
				<DisplayField 
					icon={<HomeWork className="w-5 h-5" />} 
					label="Personal Address" 
					value={businessDetails?.person?.address} 
				/>
			</div>

			<Divider sx={{ my: 3 }} />

			{/* KYC Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				KYC Details
			</Typography>

			<div className="mb-3">
				<DisplayField 
					icon={<Public className="w-5 h-5" />} 
					label="Employer Type" 
					value={businessDetails?.employerType ? 
						businessDetails.employerType.charAt(0).toUpperCase() + businessDetails.employerType.slice(1) : 
						undefined
					} 
				/>
			</div>

			<div className="mb-3">
				<DisplayField 
					icon={<Tag className="w-5 h-5" />} 
					label="Tax Number" 
					value={businessDetails?.ntn} 
				/>
			</div>

			{businessDetails?.certificateOfIncorporation && (
				<div className="mb-3">
					<DisplayField 
						icon={<Description className="w-5 h-5" />} 
						label="Certificate of Incorporation" 
						value={businessDetails?.certificateOfIncorporation} 
					/>
				</div>
			)}
		</Box>
	);
};

export default ProfileDisplay;
